import React, { useState, useEffect, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { Button, Flex, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import useScreenSize from 'hooks/useScreenSize'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useTheme } from 'styled-components'
import { BigNumber } from 'ethers'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { LoadingPlaceholder } from './LoadingPlaceholder'

dayjs.extend(duration)

interface StakeButtonProps {
  nextReleaseTimestamp: number
  isLoading: boolean
  currentToClaim: BigNumber
  claimStake: () => Promise<void>
  fetchAllData: (account?: string) => Promise<void>
  totalToClaim: BigNumber
  nextToClaim: BigNumber
  targetChainId: number
}

const StakeButton: React.FunctionComponent<StakeButtonProps> = ({
  nextReleaseTimestamp,
  isLoading,
  currentToClaim,
  claimStake,
  fetchAllData,
  totalToClaim,
  nextToClaim,
  targetChainId
}) => {
  const { account, chainId: currentChainId } = useWeb3React()
  const { t } = useTranslation()
  const { isMobile } = useScreenSize()
  const { colors } = useTheme()
  const [time, setTime] = useState<number>(0)
  const { setupNetwork } = useSetupNetwork()
  const invalidNetwork = currentChainId !== targetChainId
  const nothingToClaim = totalToClaim?.isZero() && nextToClaim?.isZero() && currentToClaim?.isZero()

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined
    if (currentToClaim?.isZero() && nextToClaim.gt(0)) {
      setTime(dayjs.unix(nextReleaseTimestamp).diff(dayjs(), 'second'))
      timer = setInterval(() => {
        const currentTime = dayjs().unix()
        setTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0))
        if (currentTime >= nextReleaseTimestamp) {
          clearInterval(timer)
          fetchAllData(account)
        }
      }, 10000)
    }
    return () => {
      if (timer !== undefined) clearInterval(timer)
    }
  }, [currentToClaim, nextReleaseTimestamp, nextToClaim, account])

  const formatDuration = (seconds: number) => {
    const milliseconds = seconds * 1000
    const durationTime = dayjs.duration(milliseconds)
    const days = durationTime.days()
    const formatString = days > 0 ? 'DD[d] HH[h] mm[m] ' : 'HH[h] mm[m] '
    return durationTime.format(formatString)
  }

  const buttonProperties = useMemo(() => {
    if (invalidNetwork) {
      return {
        disabled: false,
        description: t('stakingPanel.switchNetwork')
      }
    }
    if (nothingToClaim) {
      return {
        disabled: true,
        description: t('stakingPanel.nothingToClaim')
      }
    }
    if (time > 0 && nextToClaim?.gt(0) && currentToClaim?.isZero()) {
      return {
        disabled: true,
        description: `${t('stakingPanel.claimAvailableIn')} ${formatDuration(time)}`
      }
    }
    if (currentToClaim?.isZero()) {
      return { disabled: true, description: t('stakingPanel.claim') }
    }
    return {
      disabled: false,
      description: t('stakingPanel.claim')
    }
  }, [invalidNetwork, time, currentToClaim, isLoading, nothingToClaim, nextToClaim, totalToClaim])

  const handleClaimStake = useCallback(async () => {
    if (invalidNetwork) {
      await setupNetwork({ chainId: targetChainId, modal: true, error: false })
      return
    }
    await claimStake()
    await fetchAllData(account)
  }, [account, invalidNetwork, setupNetwork, targetChainId, claimStake, fetchAllData])

  return (
    <Flex fullWidth>
      {isLoading ? (
        <Flex flexDirection="column" gap="4px" alignItems={isMobile ? 'flex-start' : 'flex-end'}>
          <LoadingPlaceholder width="150px" height="30px" />
        </Flex>
      ) : (
        <Button
          fullWidth={isMobile}
          size={isMobile ? 'md' : 'sm'}
          disabled={buttonProperties.disabled}
          padding={isMobile ? '8px' : '16px 24px'}
          onClick={handleClaimStake}
        >
          <Text
            variant={TextVariant.BUTTON}
            color={buttonProperties.disabled ? colors.white4 : colors.white}
            style={{ cursor: buttonProperties.disabled ? 'not-allowed' : 'pointer' }}
          >
            {buttonProperties.description}
          </Text>
        </Button>
      )}
    </Flex>
  )
}

export default StakeButton
